import type { ScreeningProviderConfiguration } from "@repo/models";

import { Button, ButtonColor, Column } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { ConfigurationsFilter } from "../types";

import BackgroundChecksApi, { controller } from "../../../api/backgroundChecksApi";
import { QUERY_KEYS } from "../../../api/queries";
import { TLPGrid } from "../../../components/tlpGrid";

interface Props {
	filters: ConfigurationsFilter;
	setFilters: (filters: ConfigurationsFilter) => void;
	onEdit: (config: ScreeningProviderConfiguration) => void;
	onNew: () => void;
}

export function ConfigurationsGrid({ filters, setFilters, onEdit, onNew }: Props) {
	const configurationsQuery = useQuery({
		queryKey: [QUERY_KEYS.BackgroundChecks.Configurations, filters],
		queryFn: ({ signal }) => BackgroundChecksApi.getConfigurations(filters, { signal }),
	});

	const columns = useMemo(
		() =>
			[
				new Column("Actions")
					.withFormat((row: ScreeningProviderConfiguration) => (
						<Button color={ButtonColor.Green} size="medium" onClick={() => onEdit(row)}>
							Edit
						</Button>
					))
					.withMinWidth(100),
				new Column("Marketer", "marketer").withMinWidth(200),
				new Column("Screening Provider", "screeningProvider").withMinWidth(200),
				new Column("Products Count", "productsCount").withMinWidth(120),
			] as Column<ScreeningProviderConfiguration>[],
		[onEdit],
	);

	return (
		<TLPGrid<ScreeningProviderConfiguration, ConfigurationsFilter>
			id="backgroundChecksConfigurationsGrid"
			title="Configurations"
			exportable={false}
			changeVisibility={false}
			canSetItemsPerPage={false}
			keyProperty="marketerId"
			filters={filters}
			setFilters={setFilters}
			query={configurationsQuery}
			apiRoute={controller}
			columns={columns}
			additionalActions={<Button onClick={onNew}>New</Button>}
			maxHeight="60vh"
		/>
	);
}
